import { faCheck } from "@fortawesome/pro-regular-svg-icons/faCheck";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useEffectEvent, useRef, useState } from "react";

import "../assets/scss/components/_message-card-section-new.scss";
import { ProductApi } from "../api/ProductApi";
import Config from "../Config";
import { useAppContext } from "../contexts/AppContext";
import { useAppStore } from "../stores/AppStore";
import { useCartStore } from "../stores/CartStore";
import { useHotelStore } from "../stores/HotelStore";
import { useProductStore } from "../stores/ProductStore";
import { DateDifference } from "../utilities/DateUtils";
import { uniqid } from "../utilities/uniqid";
import { MessageCardAnimation } from "./MessageCardAnimation";
import { RecordAMessageSection } from "./RecordAMessageSection";
import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons/faInfoCircle";

type PersonalisationPayload = {
	orderReference?: string;
	frontPageThumbnailUrl?: string;
	message?: string;
	size?: string;
	[key: string]: any;
};

type PersonalisationSdkOptions = {
	brand: string;
	origin: string;
	apiKey: string;
	orderReference: string;
	disabledSizes?: string[];
	free?: boolean;
	onConfirm?: (payload: PersonalisationPayload, sessionId: string) => void | Promise<void>;
	onSave?: (payload: PersonalisationPayload, sessionId: string) => void | Promise<void>;
	onError: (payload: PersonalisationPayload) => void;
};

type PersonalisationSdk = {
	open: (options: PersonalisationSdkOptions) => { close: () => void };
	edit: (options: PersonalisationSdkOptions) => { close: () => void };
};

const personalisationSdkUrl = `${Config.personalisationOrigin}/assets/js/personalisation.v1.min.js`;
let personalisationSdkPromise: Promise<PersonalisationSdk> | null = null;

const loadPersonalisationSdk = () => {
	if (typeof window === "undefined") return Promise.reject(new Error("Personalisation is only available in the browser."));

	const existingSdk = (window as typeof window & { PGPersonalisation?: PersonalisationSdk }).PGPersonalisation;
	if (existingSdk) return Promise.resolve(existingSdk);
	if (personalisationSdkPromise) return personalisationSdkPromise;

	personalisationSdkPromise = new Promise((resolve, reject) => {
		const script = document.createElement("script");
		script.src = personalisationSdkUrl;
		script.async = true;
		script.onload = () => {
			const sdk = (window as typeof window & { PGPersonalisation?: PersonalisationSdk }).PGPersonalisation;
			if (sdk) resolve(sdk);
			else reject(new Error("The personalisation service did not initialise."));
		};
		script.onerror = () => reject(new Error("The personalisation service could not be loaded."));
		document.head.appendChild(script);
	}).catch((error) => {
		personalisationSdkPromise = null;
		throw error;
	});

	return personalisationSdkPromise;
};

export const MessageCardSectionNew = () => {
	const [charsLeft, setCharsLeft] = useState(200);
	const [personalisationLaunchRequest, setPersonalisationLaunchRequest] = useState(0);
	const [messageCardSelected, setMessageCardSelected] = useState(false);
	const [openVideoMessage, setOpenVideoMessage] = useState(false);
	const [personalisationError, setPersonalisationError] = useState("");
	const personalisationOrderReference = useRef(uniqid("PC"));
	
	const appData = useAppContext();
	const { cutoffs: storedCutoffs } = useAppStore(({ cutoffs }) => ({ cutoffs }));
	const cutoffs = appData?.cutoffs || storedCutoffs;

	const {
		SetMessageCardText,
		SetMessageCardType,
		messageCardType,
		messageCardText,
		SetPrintzwareId,
		SetPersonalisation,
		printzwareId,
		personalisation,
		SetPrice,
		selectedDeliveryDate,
		info,
		videoMsg,
	} = useProductStore((state) => ({
		SetMessageCardText: state.SetMessageCardText,
		SetMessageCardType: state.SetMessageCardType,
		messageCardType: state.messageCardType,
		messageCardText: state.messageCardText,
		SetPrintzwareId: state.SetPrintzwareId,
		SetPersonalisation: state.SetPersonalisation,
		printzwareId: state.printzwareId,
		personalisation: state.personalisation,
		SetPrice: state.SetPrice,
		selectedDeliveryDate: state.selectedDeliveryDate,
		videoMsg: state.videoMsg,
		info: state.info,
	}));
	const { hotelFlow } = useHotelStore((state) => ({
		hotelFlow: state.hotelFlow,
	}));

	const { GetCart } = useCartStore((state) => state);

	useEffect(() => {
		GetCart();
	}, [GetCart]);

	const ChangeCardType = (type, value = false) => {
		if (type === "standard") {
			SetMessageCardType("standard");
			setMessageCardSelected("standard");
			SetPrintzwareId(null);
			SetPersonalisation(null);

			if (value !== false) {
				SetMessageCardText(value || "");
			}
		} else {
			SetMessageCardType("personalised");
			setMessageCardSelected("personalised");
			SetMessageCardText("");
			if (value) {
				SetPersonalisation(value);
				SetPrintzwareId(value.order_reference);
			}
		}

		SetPrice();
	};

	const openPersonalisation = () => {
		setPersonalisationError("");
		setPersonalisationLaunchRequest((request) => request + 1);
	};
	const resetPersonalisationOrderReference = () => {
		personalisationOrderReference.current = uniqid("PC");
	};

	let currentTime = new Date();
	let PersonalisedCardCheck = true;

	const cutoff = cutoffs?.cards?.days?.[currentTime.getDay()];

	PersonalisedCardCheck = (
		DateDifference(selectedDeliveryDate, currentTime).days === 0 &&
		cutoff === null ||
		((currentTime.getHours() == cutoff?.h && currentTime.getMinutes() >= cutoff?.m) ||
		currentTime.getHours() > cutoff?.h)
	) ? false : PersonalisedCardCheck;
	PersonalisedCardCheck =
		info?.distributor !== "julian" && info?.distributor !== "the_original_baker" ? false : PersonalisedCardCheck;
	PersonalisedCardCheck = !Config.personalisedCardEnabled ? false : PersonalisedCardCheck;

	const selectedCardPrice = personalisation ? parseFloat(personalisation.price ?? 0) : null;
	const cardPriceLabel =
		selectedCardPrice === 0 || (selectedCardPrice === null && info?.free_card)
			? "FREE"
			: `${selectedCardPrice === null ? "From " : ""}+£${(selectedCardPrice ?? Config.personalisedCardPrice).toFixed(2)}`;

	return (
		<section className="message-card-section">
			<strong className="section-title">
				{hotelFlow || false ? "Leave a note with your gift:" : "Let them know who to thank:"}
			</strong>
			<p>
				{hotelFlow || false
					? "We will include a card which will be presented along time your gift."
					: "All gifts include a free message card. Type your message, and we will print and send it with your item."}
			</p>

			<div className="message-card-options">
				{personalisationError && <p role="alert">{personalisationError}</p>}
				{/* Standard Free Message Card */}
				<div
					className={`message-card-option ${messageCardType === "standard" ? "active" : ""}`}
					onClick={() => ChangeCardType("standard")}
				>
					<div className="message-card-option-text">
						<strong>Free Message Card</strong>
						<p>
							All gifts include a free message card. Type your message, and we will print and send it with
							your item.
						</p>
					</div>
					<div className="message-check">
						<div className="checkmark">
							<FontAwesomeIcon icon={faCheck} />
						</div>
						<strong className="price">FREE</strong>
					</div>

					{messageCardType === "standard" && (
						<div className="col-12 free-message-card-choice">
							<strong>Free Message Card</strong>
							<p>Type your message in the field or choose a personalised option below.</p>
							<div className="field">
								<div className="textarea-container">
									<textarea
										id="card-message"
										maxLength="200"
										rows="2"
										value={messageCardText}
										onChange={(e) => {
											setCharsLeft(200 - e.target.value.length);
											ChangeCardType("standard", e.target.value);
										}}
										disabled={messageCardType === "personalised"}
									/>
									<label>Card Message:</label>
									<div className="remaining-characters-section">
										<span className="remaining-characters">{charsLeft}</span> Characters Remaining
									</div>
								</div>
							</div>
						</div>
					)}
				</div>

				{PersonalisedCardCheck && (
					<div className={`message-card-option ${messageCardType === "personalised" ? "active" : ""}`}>
						<div className="message-card-option-text" onClick={() => ChangeCardType("personalised")}>
							<strong>Personalised Greeting Card</strong>
							<p>
								High-quality customised greeting card. Choose the size and design, then customise it
								for your recipient.
							</p>
						</div>
						<div className="message-check" onClick={() => ChangeCardType("personalised")}>
							<div className="checkmark">
								<FontAwesomeIcon icon={faCheck} />
							</div>
							<strong className="price">{cardPriceLabel}</strong>
						</div>

						{messageCardType === "personalised" && (
							<div className="col-12">
								<div className="personalised-card-choice">
									<div className="text-content" onClick={openPersonalisation}>
										<strong className="personalised-card-title">
											Get A Personalised Greeting Card
											
											<span className="delivery-deadline">
												<FontAwesomeIcon icon={faInfoCircle} />
												Order by 6pm for Personalised Card Delivery
												</span>
											</strong>
										{!printzwareId ? (
											<MessageCardAnimation />
										) : (
											<div
												className="personalised-card-cover card-added"
												style={{
													backgroundImage: `url('${personalisation?.front_page_thumbnail_url || Config.getPersonalisedCardPreviewUrl(printzwareId)}')`,
												}}
												onClick={openPersonalisation}
											></div>
										)}
										<button
											className="get-started"
											onClick={(event) => {
												event.stopPropagation();
												openPersonalisation();
											}}
										>
											{!printzwareId ? "Get Started" : "Edit Card"}
										</button>
									</div>
								</div>
							</div>
						)}
					</div>
				)}

				{((messageCardSelected === "personalised" && printzwareId) ||
					(messageCardSelected === "standard" && messageCardText !== "")) && (
					<>
						<strong className="also-add"> You can also add: </strong>

						<div className={`message-card-option ${videoMsg || openVideoMessage ? "active" : ""}`}>
							<div
								className="message-card-option-text"
								onClick={() => {
									setOpenVideoMessage(!openVideoMessage);
								}}
							>
								<strong>
									Video / Voice Message <span>New!</span>
								</strong>
								<p>
									Record a video or audio message to send with your gift. You can record a message up
									to 30 seconds long, and we will include it on your message card.
								</p>
							</div>
							<div
								className="message-check"
								onClick={() => {
									setOpenVideoMessage(!openVideoMessage);
								}}
							>
								<div className="checkmark">
									<FontAwesomeIcon icon={faCheck} />
								</div>
								<strong className="price">FREE</strong>
							</div>

							{openVideoMessage && <RecordAMessageSection />}
						</div>
					</>
				)}
			</div>

			<PersonalisedCardModal
				launchRequest={personalisationLaunchRequest}
				orderReference={printzwareId || personalisationOrderReference.current}
				isEditing={Boolean(printzwareId)}
				isFree={Boolean(info?.free_card)}
				onClose={resetPersonalisationOrderReference}
				onError={setPersonalisationError}
				personalisedCardCallback={async (payload) => {
					const preparedPersonalisation = await ProductApi.preparePersonalisation(info.id, payload);
					setPersonalisationError("");
					ChangeCardType("personalised", preparedPersonalisation);
				}}
			/>
		</section>
	);
};

const PersonalisedCardModal = ({
	launchRequest,
	orderReference,
	isEditing,
	isFree,
	onClose,
	onError,
	personalisedCardCallback,
}) => {
	const handleSuccess = useEffectEvent((payload: PersonalisationPayload) => {
		return personalisedCardCallback(payload);
	});
	const handleClose = useEffectEvent(onClose);
	const handleError = useEffectEvent((payload: PersonalisationPayload) => {
		onError(payload.message || "Unable to personalise your card. Please try again.");
	});

	const launchPersonalisation = useEffectEvent(() => {
		let active = true;
		let closeHandled = false;
		let closeModal: (() => void) | undefined;
		let closeObserver: MutationObserver | undefined;
		const notifyClose = () => {
			if (closeHandled) return;
			closeHandled = true;
			handleClose();
		};
		const handleConfirmation = async (payload: PersonalisationPayload) => {
			await handleSuccess(payload);
			notifyClose();
		};

		if (!Config.personalisationApiKey) {
			handleError({ message: "Personalised cards are temporarily unavailable." });
			return;
		}

		loadPersonalisationSdk()
			.then((sdk) => {
				if (!active) return;

				const options: PersonalisationSdkOptions = {
					origin: Config.personalisationOrigin,
					apiKey: Config.personalisationApiKey,
					orderReference,
					free: isFree,
					brand: Config.personalisationBrand,
					onError: handleError,
				};

				const modal = isEditing
					? sdk.edit({ ...options, onSave: handleConfirmation })
					: sdk.open({ ...options, onConfirm: handleConfirmation });
				closeModal = modal.close;

				const modalElement = document.getElementById("pg-personalisation-modal");
				if (modalElement) {
					closeObserver = new MutationObserver(() => {
						if (active && !modalElement.isConnected) {
							closeObserver?.disconnect();
							notifyClose();
						}
					});
					closeObserver.observe(document.body, { childList: true });
				}
			})
			.catch((error) => handleError({ message: error instanceof Error ? error.message : String(error) }));

		return () => {
			active = false;
			closeObserver?.disconnect();
			closeModal?.();
		};
	});

	useEffect(() => {
		if (!launchRequest) return;

		return launchPersonalisation();
	}, [launchRequest]);

	return null;
};
